import React, { useState, useEffect, useCallback } from 'react';
import { 
  MagnifyingGlassIcon, 
  MapPinIcon,
} from '@heroicons/react/24/outline';
import { SearchFilters } from '../types/api.types';
import { useCategories } from '../hooks/useCategories';
import { categoryIcons } from '../utils/constants';
import { useGeolocation } from '../context/GeolocationContext';
import { useToast } from './common/Toast';

interface CompactHeroSearchProps {
  onSearch: (filters: SearchFilters) => void;
  isSearchApplied: boolean;
}

const CompactHeroSearch: React.FC<CompactHeroSearchProps> = ({ onSearch, isSearchApplied }) => {

  const { categories, loading: categoriesLoading } = useCategories();
  const { location: userLocation, loading: geoLoading, requestPreciseLocation } = useGeolocation();
  const toast = useToast();

  const handleDetectLocation = useCallback(() => {
    requestPreciseLocation((errorMsg) => {
      toast.error(errorMsg);
    });
  }, [requestPreciseLocation, toast]);

    const popularCategories = categories.map(cat => ({
      name: cat.label,
      icon: categoryIcons[cat.value] || '📋',
      value: cat.value,
    }));
  
  const [searchQuery, setSearchQuery] = useState('');
  const [location, setLocation] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');

  // Clear all filters function
  const clearAllFilters = useCallback(() => {
    setSearchQuery('');
    setLocation('');
    setSelectedCategory('');
  }, []);
  
  useEffect(() => {
    if (!isSearchApplied) {
      clearAllFilters();
    }
  }, [clearAllFilters, isSearchApplied]);

  // Debounced search effect
  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      let filters: SearchFilters = {};
      filters.query = searchQuery?.trim() || undefined;
      if (location.trim() || selectedCategory) {
        filters = {
          ...filters,
          location: location.trim() || undefined,
          category: selectedCategory || undefined,
        };
      }
      onSearch(filters);
    }, 500);

    return () => clearTimeout(debounceTimer);
  }, [searchQuery, location, selectedCategory, onSearch]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    
    const filters: SearchFilters = {
      query: searchQuery.trim() || undefined,
      location: location.trim() || undefined,
      category: selectedCategory || undefined,
    };
    onSearch(filters);
  };

  const handleCategoryClick = (categoryValue: string) => {
    setSelectedCategory(categoryValue);
    const filters: SearchFilters = {
      category: categoryValue,
      query: searchQuery.trim() || undefined,
      location: location.trim() || undefined,
    };
    onSearch(filters);
  };

  return (
    <section className="relative bg-gradient-to-br from-primary-600 via-accent-600 to-indigo-700 text-white overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0" style={{
          backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
          backgroundSize: '40px 40px'
        }} />
      </div>

      <div className="relative max-w-7xl mx-auto px-3 xs:px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        {/* Header */}
        <div className="text-center mb-4 sm:mb-6">
          <h1 className="text-2xl xs:text-3xl sm:text-4xl lg:text-5xl font-extrabold mb-2 sm:mb-3 leading-tight">
            Find & Book Services
            <span className="block text-yellow-300">Instantly</span>
          </h1>
          <p className="text-white/90 text-sm xs:text-base sm:text-lg max-w-2xl mx-auto px-2">
            Discover top-rated professionals and book appointments in seconds
          </p>
        </div>

        {/* Search Form */}
        <form onSubmit={handleSearch} className="max-w-5xl mx-auto">
          <div className="bg-white/95 backdrop-blur-sm rounded-xl sm:rounded-2xl shadow-2xl p-3 sm:p-4">
            <div className="flex flex-col xs:flex-row gap-2 sm:gap-3">
              {/* Search Input */}
              <div className="flex-1 flex items-center px-3 sm:px-4 py-2.5 sm:py-3 bg-gray-50 rounded-lg sm:rounded-xl border border-gray-200 focus-within:ring-2 focus-within:ring-primary-500">
                <MagnifyingGlassIcon className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400 mr-2 flex-shrink-0" />
                <input
                  type="text"
                  placeholder="Search services or providers..."
                  aria-label="Search services or providers"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full bg-transparent text-sm sm:text-base text-gray-900 placeholder-gray-500 focus:outline-none"
                />
              </div>

              {/* City Input + Precise Location Button */}
              <div className="flex gap-1.5 sm:gap-2 xs:max-w-[280px] sm:max-w-sm">
                <div className="flex items-center px-3 sm:px-4 py-2.5 sm:py-3 bg-gray-50 rounded-lg sm:rounded-xl border border-gray-200 focus-within:ring-2 focus-within:ring-primary-500">
                  <MapPinIcon className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400 mr-2 flex-shrink-0" />
                  <input
                    type="text"
                    placeholder={userLocation?.city || 'Enter city name...'}
                    aria-label="City or location"
                    value={location}
                    onChange={(e) => setLocation(e.target.value)}
                    className="w-full bg-transparent text-sm sm:text-base text-gray-900 placeholder-gray-500 focus:outline-none"
                  />
                </div>

                {/* Precise Location Button */}
                {userLocation?.source !== 'precise' && (
                  <button
                    type="button"
                    onClick={handleDetectLocation}
                    disabled={geoLoading}
                    className="flex-shrink-0 px-3 sm:px-4 py-2.5 sm:py-3 bg-primary-50 text-primary-600 rounded-lg sm:rounded-xl border border-primary-200 hover:bg-primary-100 transition-colors disabled:opacity-50 text-xs sm:text-sm font-medium whitespace-nowrap"
                  >
                    {geoLoading ? 'Locating...' : 'Detect Current Location'}
                  </button>
                )}
              </div>
            </div>
          </div>
        </form>

        {/* Popular Categories - Compact */}
        <div className="mt-4 sm:mt-6 max-w-5xl mx-auto">
          <div className="flex flex-wrap justify-center gap-1.5 xs:gap-2">
            {categoriesLoading ? (
              <>
                {Array.from({ length: 6 }).map((_, i) => (
                  <div
                    key={i}
                    className="animate-pulse flex items-center justify-center space-x-1 xs:space-x-1.5 h-8 xs:h-9 sm:h-10 min-w-[7.5rem] xs:min-w-[8rem] sm:min-w-[9.5rem] px-2.5 xs:px-3 sm:px-4 rounded-full bg-white/10 border border-white/20"
                  >
                    <div className="w-[14px] h-[14px] xs:w-4 xs:h-4 bg-white/20 rounded-full flex-shrink-0" />
                    <div className="h-3 xs:h-3.5 bg-white/20 rounded w-14 xs:w-16 sm:w-20" />
                  </div>
                ))}
              </>
            ) : (
              popularCategories.map((category) => (
              <button
                key={category.value}
                onClick={() => handleCategoryClick(category.value)}
                className={`flex items-center justify-center space-x-1 xs:space-x-1.5 h-8 xs:h-9 sm:h-10 min-w-[7.5rem] xs:min-w-[8rem] sm:min-w-[9.5rem] px-2.5 xs:px-3 sm:px-4 rounded-full transition-all duration-300 border ${
                  selectedCategory === category.value
                    ? 'bg-white text-primary-600 border-white shadow-lg scale-105'
                    : 'bg-white/10 backdrop-blur-sm text-white border-white/20 hover:bg-white/20 hover:scale-105'
                }`}
              >
                <span className="text-sm xs:text-base">{category.icon}</span>
                <span className="text-xs xs:text-sm font-medium">{category.name}</span>
              </button>
            ))
            )}
          </div>
        </div>

        {/* Mini Stats */}
        <div className="mt-4 sm:mt-6 flex justify-center gap-4 xs:gap-6 sm:gap-10 text-sm">
          <div className="text-center">
            <div className="text-lg xs:text-xl sm:text-2xl font-bold">10K+</div>
            <div className="text-white/80 text-[10px] xs:text-xs">Providers</div>
          </div>
          <div className="text-center">
            <div className="text-lg xs:text-xl sm:text-2xl font-bold">1M+</div>
            <div className="text-white/80 text-[10px] xs:text-xs">Bookings</div>
          </div>
          <div className="text-center">
            <div className="text-lg xs:text-xl sm:text-2xl font-bold">4.8★</div>
            <div className="text-white/80 text-[10px] xs:text-xs">Rating</div>
          </div>
        </div>
      </div>

      {/* Wave Separator */}
      <div className="absolute bottom-0 left-0 right-0">
        <svg
          viewBox="0 0 1440 60"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          className="w-full h-auto"
        >
          <path
            d="M0 60L60 55C120 50 240 40 360 35C480 30 600 30 720 32.5C840 35 960 40 1080 42.5C1200 45 1320 45 1380 45L1440 45V60H1380C1320 60 1200 60 1080 60C960 60 840 60 720 60C600 60 480 60 360 60C240 60 120 60 60 60H0Z"
            fill="rgb(249, 250, 251)"
          />
        </svg>
      </div>
    </section>
  );
};

export default CompactHeroSearch;
